









import { computed, defineComponent, ref } from "@vue/composition-api";
import { SmsCategory } from "@scrile/api-provider/dist/api/SmsNotificationsProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import router from "@/router";
import { FormFields, FormValues } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";

const categoryByField: Record<string, SmsCategory> = {
  incomingMessages: SmsCategory.INCOMING_MESSAGES,
  participantRequests: SmsCategory.PARTICIPANT_REQUESTS,
  participantResults: SmsCategory.PARTICIPANT_RESULTS,
  accountStatus: SmsCategory.ACCOUNT_STATUS,
  newSubscription: SmsCategory.NEW_SUBSCRIPTION,
};

export default defineComponent({
  name: "PageSmsNotificationsSettings",
  components: { BasePage, PageTitle, BaseForm, BaseButton },
  setup() {
    const { userVerificationStatus, userVerificationUpgradeStatus } = useAuthUser();
    const loading = ref(true);
    const saving = ref(false);
    const initialEnabled = ref(false);
    const values = ref<FormValues>({ enabled: false });

    // Roles are granted already on application submit (even both at once), so only the verification
    // statuses tell what the user is approved for: status = limited, upgradeStatus = full
    const showCreatorCategories = computed(() => userVerificationStatus.value === UserVerificationStatus.VERIFIED);
    const showFullCategories = computed(() => userVerificationUpgradeStatus.value === UserVerificationStatus.VERIFIED);

    const categoryFields = computed<FormFields>(() => [
      ...(showCreatorCategories.value
        ? [
            {
              class: "__incoming-messages mt-0 mb-1",
              name: "incomingMessages",
              label: $t("Incoming messages from other creators"),
              type: "switch",
            },
            {
              class: "__participant-requests mt-0 mb-1",
              name: "participantRequests",
              label: $t("Participant form requests"),
              type: "switch",
            },
          ]
        : []),
      ...(showFullCategories.value
        ? [
            {
              class: "__participant-results mt-0 mb-1",
              name: "participantResults",
              label: $t("Participant form results"),
              type: "switch",
            },
          ]
        : []),
      {
        class: "__account-status mt-0 mb-1",
        name: "accountStatus",
        label: $t("Changes to account status"),
        type: "switch",
      },
      ...(showFullCategories.value
        ? [
            {
              class: "__new-subscription mt-0 mb-1",
              name: "newSubscription",
              label: $t("New fan subscription"),
              type: "switch",
            },
          ]
        : []),
    ]);

    const fields = computed<FormFields>(() => [
      {
        class: "__enabled mt-0 mb-1",
        name: "enabled",
        label: $t("Receive SMS notifications"),
        type: "switch",
      },
      ...(values.value.enabled ? categoryFields.value : []),
    ]);

    const init = async () => {
      try {
        const [consent, { settings }] = await Promise.all([
          providers.SmsNotificationsProvider.consentFind(),
          providers.SmsNotificationsProvider.settingsFind(),
        ]);
        initialEnabled.value = consent.enabled;
        values.value = {
          enabled: consent.enabled,
          ...Object.fromEntries(
            Object.entries(categoryByField).map(([name, category]) => [name, settings[category] ?? true])
          ),
        };
      } finally {
        loading.value = false;
      }
    };

    const onSave = async () => {
      try {
        saving.value = true;
        const settings = Object.fromEntries(
          categoryFields.value.map(field => [categoryByField[field.name], Boolean(values.value[field.name])])
        );
        await providers.SmsNotificationsProvider.settingsUpdate({ data: { settings } });
        if (Boolean(values.value.enabled) !== initialEnabled.value) {
          await providers.SmsNotificationsProvider.consentUpdate({ enabled: Boolean(values.value.enabled) });
        }
        router.back();
      } finally {
        saving.value = false;
      }
    };

    init();

    return { loading, saving, fields, values, onSave };
  },
});
